import { useMemo } from 'react'
import { Field, FieldHelp, FieldLabel, Input } from '@axilon-apps/libs-ui-shadcn'
import { z as zod } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import {
  FlashMessages,
  PasswordInput,
  calculatePasswordStrength,
  LoadingButton
} from '@axilon-apps/libs-ui-components'
import { passwordStrengthRequirement } from '@axilon-apps/domains-identity/shared/core'

import { FlashMessageGroup } from '../../../../constants/flash-message-groups'

interface Props {
  onSubmit: (data: FormData) => Promise<boolean>
  errorResponse: unknown
  isLoading: boolean
  showOrganizationSlug?: boolean
}

const baseSchema = zod.object({
  organizationSlug: zod.string(),
  username: zod.string().min(1, { message: 'Username is required' }),
  recoveryCode: zod.string().min(1, { message: 'Recovery Code required' }),
  newPassword: zod
    .string()
    .min(1, { message: 'New password is required' })
    .refine(
      (password) => calculatePasswordStrength(password).strength >= passwordStrengthRequirement,
      {
        message: `Password requires complexity level 'Strong' or above!`
      }
    ),
  confirmPassword: zod.string().min(1, { message: 'Confirm password is required' })
})

const schema = (organizationRequired: boolean) =>
  baseSchema
    .extend({
      organizationSlug: organizationRequired
        ? zod.string().min(1, { message: 'Organization is required' })
        : zod.string()
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: 'Passwords do not match',
      path: ['confirmPassword']
    })

export type FormData = zod.infer<typeof baseSchema>

const AccountRecoveryForm = ({ isLoading, onSubmit, showOrganizationSlug = false }: Props) => {
  const defaultValues = {
    organizationSlug: '',
    newPassword: '',
    recoveryCode: '',
    username: '',
    confirmPassword: ''
  } satisfies FormData

  const resolver = useMemo(() => zodResolver(schema(showOrganizationSlug)), [showOrganizationSlug])

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch
  } = useForm<FormData>({
    defaultValues,
    resolver
  })

  const submit = async ({ newPassword, recoveryCode, username, organizationSlug }: FormData) => {
    const success = await onSubmit({
      newPassword,
      recoveryCode,
      username,
      organizationSlug
    } as FormData)

    if (success) {
      reset(defaultValues)
    }
  }

  return (
    <form onSubmit={handleSubmit(submit)}>
      <div className="flex flex-col gap-4">
        {showOrganizationSlug && (
          <Controller
            control={control}
            name="organizationSlug"
            render={({ field }) => (
              <Field data-invalid={Boolean(errors.organizationSlug) || undefined}>
                <FieldLabel required>Organization</FieldLabel>
                <Input {...field} type="text" />
                {errors.organizationSlug ? (
                  <FieldHelp className="text-destructive">
                    {errors.organizationSlug.message}
                  </FieldHelp>
                ) : null}
              </Field>
            )}
          />
        )}
        <Controller
          control={control}
          name="username"
          render={({ field }) => (
            <Field data-invalid={Boolean(errors.username) || undefined}>
              <FieldLabel required>Username</FieldLabel>
              <Input {...field} type="text" />
              {errors.username ? (
                <FieldHelp className="text-destructive">{errors.username.message}</FieldHelp>
              ) : null}
            </Field>
          )}
        />
        <PasswordInput
          control={control}
          name="recoveryCode"
          error={errors.recoveryCode?.message}
          label="Recovery code"
          showComplexity={false}
          watch={watch}
        />
        <PasswordInput
          control={control}
          name="newPassword"
          error={errors.newPassword?.message}
          label="New Password"
          watch={watch}
        />
        <PasswordInput
          control={control}
          name="confirmPassword"
          error={errors.confirmPassword?.message}
          label="Confirm Password"
          watch={watch}
        />
        <FlashMessages group={FlashMessageGroup.RecoverAccount} />
        <LoadingButton loading={isLoading} type="submit">
          Recover Account
        </LoadingButton>
      </div>
    </form>
  )
}

export default AccountRecoveryForm
